import { ListingsLoadingState } from "@/components/listings/ListingsLoadingState";
import { Card } from "@/components/ui/card";
import { useListing } from "@/hooks/useListings";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Listing } from "@/lib/types"; // Added this import

// Import refactored components
import PropertyImageGrid from "@/components/property-details/PropertyImageGrid";
import PropertySpecs from "@/components/property-details/PropertySpecs";
import ContactSpecialists from "@/components/property-view/ContactSpecialists";
import PropertyMainContent from "@/components/property-view/PropertyMainContent";
import PropertyNotFound from "@/components/property-view/PropertyNotFound";
import PropertySidebar from "@/components/property-view/PropertySidebar";
import PropertyViewHeader from "@/components/property-view/PropertyViewHeader";
import SimilarProperties from "@/components/property-view/SimilarProperties";

const PropertyView = () => {
  const { id } = useParams<{ id: string }>();
  const [isFavorite, setIsFavorite] = useState(false);

  // If ID is not available yet, show loading state
  if (!id) {
    console.log("PropertyView: ID is undefined, showing loading state.");
    return <ListingsLoadingState />;
  }

  console.log("PropertyView: Property ID from URL params:", id);

  // Get the listing data
  // Now 'id' is guaranteed to be a string here
  const { data, status, error } = useListing(id);

  // Error handling
  useEffect(() => {
    if (error) {
      console.error("PropertyView: Property view error:", error);
      toast.error("Failed to load property details");
    }
  }, [error]);

  // Log data received from API for debugging
  useEffect(() => {
    if (data) {
      console.log("PropertyView: Property data received:", data);
    }
  }, [data]);

  console.log(
    "PropertyView: Current state - status:",
    status,
    "error:",
    error,
    "raw data:",
    data,
    "data?.listing:",
    data?.listing,
    "data?.images:",
    data?.images
  );

  // If data is explicitly null or undefined, always show loading first.
  // This handles the case where status might be 'success' but data is not yet populated.
  if (data === undefined || data === null) {
    console.log(
      "PropertyView: Rendering ListingsLoadingState (data is undefined/null)."
    );
    return <ListingsLoadingState />;
  }

  // If there's an error, show PropertyNotFound
  if (error) {
    console.log("PropertyView: Rendering PropertyNotFound due to error.");
    return <PropertyNotFound />;
  }

  // Safely extract property and images, handling different data structures
  let property: Listing | undefined;
  let images: any[] = [];

  if (data) {
    property = data; // data is now directly the Listing object
    images = data.images || []; // Extract images from the Listing object
  }

  // If property is still undefined or null, or if it's an empty object (meaning no valid property data)
  if (!property || !property.id) {
    console.log(
      "PropertyView: Rendering PropertyNotFound (property is undefined/null or missing ID after successful data fetch)."
    );
    return <PropertyNotFound />;
  }
  // Amenities and features are now unified under 'attributes' in the backend response
  // The PropertyDetails component and its sub-components will handle filtering by type.

  // Handle favorite toggle
  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    toast.success(isFavorite ? "Removed from favorites" : "Added to favorites");
  };

  // Prepare image URLs for the gallery
  const imageUrls = Array.isArray(images)
    ? images.map((img) => (typeof img === "string" ? img : img.url))
    : [];

  return (
    <div className="bg-gradient-to-b from-gray-50 to-blue-50 pt-4 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Property Header with Navigation and Actions */}
        <PropertyViewHeader
          property={property}
          isFavorite={isFavorite}
          toggleFavorite={toggleFavorite}
        />
        {/* Full Width Image Gallery (Moved outside the grid) */}
        <div className="mt-6 mb-6">
          {" "}
          {/* Add margin */}
          <PropertyImageGrid
            images={imageUrls}
            title={property.title}
            isFeatured={Boolean(property.is_featured)}
            listingCode={property.cplk_ref || property.listing_code}
          />
        </div>
        {/* Main Content Grid (Below Image Gallery) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column (Specs, Main Content + Other Sections) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Card containing Specs and Main Content */}
            <Card className="bg-white shadow-md border border-blue-100">
              <PropertySpecs
                size={property.floor_size}
                sizeUnit={property.size_unit || "sq ft"}
                landSize={property.plot_size}
                landSizeUnit={property.plot_size_unit || "Perch"}
                bathrooms={property.bathrooms}
                parkingSpaces={property.parking_spaces}
              />
              <div className="p-6 border-t border-gray-100 space-y-6">
                <PropertyMainContent property={property} />
              </div>
            </Card>

            {/* Other sections below the main content card */}
            <SimilarProperties property={property} />
            <ContactSpecialists
              city={
                (property.location && (property.location as any).city) ||
                property.location?.city_name ||
                "Sri Lanka"
              }
            />
            {/* <CallToAction /> */}
          </div>

          {/* Right Column (Sticky Sidebar) */}
          <div className="lg:col-span-1 space-y-6 lg:sticky lg:top-20 self-start">
            <PropertySidebar property={property} />
          </div>
        </div>{" "}
        {/* End Main Grid */}
      </div>
    </div>
  );
};

export default PropertyView;
